import { defineStore } from 'pinia';
import { callAPI } from '@/api/httpservice';
import { usePeopleStore } from './peopleStore';
import { useRecoStore } from './recoStore';
import { useSocketStore } from './socketStore';
import { initializeKeycloak } from '@/utils/keycloak';
import i18n from '@/utils/i18n';
import { notify } from '@/utils/toaster';
import { useVideoStore } from './videoStore';
import { useFeatureStore } from './featureStore';

export const useUserStore = defineStore('user', {
  state: () => ({
    search: '',
    users: [],
    currentUser: null,
    locale: 'fr',
    page: 1,
    itemPerPage: 10,
    loading: false,
    isTheme: false,
    socket: null,
    keycloak: null,
    userActions: [],
    actionsUserId: null,
    actionsPage: 1,
    actionsLimit: 20,
    actionsTotal: 0,
    loadingActions: false,
    scraperStats: null,
  }),
  getters: {
    getTotal() {
      return Math.ceil(this.users.length / this.itemPerPage);
    },
    // Reste-t-il des actions à charger pour l'utilisateur sélectionné ?
    hasMoreActions() {
      return this.userActions.length < this.actionsTotal;
    },
    getPaginatedItems() {
      const start = (this.page - 1) * this.itemPerPage;
      const end = start + this.itemPerPage;
      return this.users.slice(start, end);
    },
    hasRole: (state) => (role) => state.currentUser?.roles?.includes(role) ?? false,
    isAdmin() {
      return this.hasRole('Admin') || this.hasRole('Weaverize');
    },
    // Le mode maintenance est piloté par le feature flag Unleash `maintenance`
    maintenanceMode() {
      return useFeatureStore().isEnabled('maintenance');
    },
  },
  actions: {
    async getUser() {
      const user = this.keycloak.tokenParsed;

      if (user) {
        this.currentUser = {
          name: user.name,
          email: user.email,
          roles: user.resource_access?.[process.env.VUE_APP_KEYCLOAK_CLIENT_ID]?.roles ?? [],
        };
        // Récupérer la locale depuis la réponse, puis localStorage en fallback
        const userLocale = user.attributes?.locale?.[0] || localStorage.getItem('locale') || 'fr';
        this.locale = userLocale;
        i18n.global.locale = userLocale;
      }
    },
    async generateToken(dto) {
      return await callAPI('POST', '/auth/tokens', dto);
    },

    // Charge la première page d'actions d'un utilisateur (réinitialise la liste).
    async getUserActions(userId) {
      this.actionsUserId = userId;
      this.actionsPage = 1;
      this.userActions = [];
      this.actionsTotal = 0;
      await this.fetchActions();
    },

    // Charge la page suivante et l'ajoute à la liste (scroll infini).
    async loadMoreActions() {
      if (this.loadingActions || !this.hasMoreActions) return;
      this.actionsPage += 1;
      await this.fetchActions();
    },

    async fetchActions() {
      if (!this.actionsUserId) return;
      this.loadingActions = true;
      await callAPI(
        'GET',
        `/action/${this.actionsUserId}?page=${this.actionsPage}&limit=${this.actionsLimit}`,
        null,
      )
        .then((res) => {
          this.userActions = this.actionsPage === 1 ? res.result : [...this.userActions, ...res.result];
          this.actionsTotal = res.total;
        })
        .catch(() => {
          notify.error(null, { description: i18n.global.t('admin.activities-error') });
        })
        .finally(() => {
          this.loadingActions = false;
        });
    },

    // Enrichit chaque utilisateur de `users` avec `lastActionAt` (date de l'action
    // la plus récente). Implémentation intérimaire : une requête `/action/{id}` par
    // utilisateur. À remplacer par un endpoint backend bulk renvoyant directement la
    // dernière date d'action par utilisateur (cf. contrat d'API) pour éviter les N requêtes.
    async getUsersLastActions() {
      await Promise.all(
        this.users.map(async (user) => {
          try {
            // Une seule action suffit : le backend renvoie les plus récentes d'abord.
            const res = await callAPI('GET', `/action/${user.id}?page=1&limit=1`, null);
            const latest = (res?.result ?? []).reduce(
              (max, a) => (a.createdAt > max ? a.createdAt : max),
              '',
            );
            user.lastActionAt = latest || null;
          } catch {
            user.lastActionAt = null;
          }
        }),
      );
    },

    async getScraperStats() {
      await callAPI('GET', '/peoples/statistics/scrapers', null)
        .then((res) => {
          this.scraperStats = res;
        })
        .catch(() => {
          notify.error(null, { description: i18n.global.t('admin.scraper-stats-error') });
        });
    },

    async initListeners() {
      const peopleStore = usePeopleStore();
      const recoStore = useRecoStore();
      const socketStore = useSocketStore();
      const videoStore = useVideoStore();

      this.getUser();
      useFeatureStore().setUser(this.keycloak.tokenParsed?.email);
      socketStore.initSocket();
      peopleStore.setupListeners();
      recoStore.setupRecoListeners();
      videoStore.setupVideoListeners();
      this.startVersionPolling();
    },

    startVersionPolling() {
      if (process.env.NODE_ENV !== 'production') return;
      const currentVersion = process.env.VUE_APP_VERSION;
      if (!currentVersion) return;

      const fetchServerVersion = async () => {
        const res = await fetch('/version.json', { cache: 'no-store' });
        const { version: serverVersion } = await res.json();
        return serverVersion ?? null;
      };

      const notifyUpdate = () => {
        this._stopVersionWatcher();
        notify.info(i18n.global.t('newVersion.title'), {
          description: i18n.global.t('newVersion.description'),
          duration: 'infinite',
          action: {
            label: i18n.global.t('newVersion.reload'),
            onClick: () => {
              const url = new URL(globalThis.location.href);
              url.searchParams.set('_cb', Date.now());
              globalThis.location.replace(url.toString());
            },
          },
        });
      };

      const checkVersion = async () => {
        try {
          const serverVersion = await fetchServerVersion();
          if (serverVersion && serverVersion !== currentVersion) notifyUpdate();
        } catch {
          // silencieux — ne pas déranger l'utilisateur si le check échoue
        }
      };

      checkVersion();

      this._versionPollInterval = setInterval(checkVersion, 5 * 60 * 1000);

      this._visibilityHandler = () => {
        if (!document.hidden) checkVersion();
      };
      document.addEventListener('visibilitychange', this._visibilityHandler);
    },

    _stopVersionWatcher() {
      clearInterval(this._versionPollInterval);
      document.removeEventListener('visibilitychange', this._visibilityHandler);
    },

    /**
     * KEYCLOAK FUNCTIONS
     */

    async setKeycloakInstance() {
      // init keycloak
      console.log('Initializing Keycloak...');
      this.keycloak = await initializeKeycloak();

      if (this.keycloak.authenticated) {
        this.initListeners();

        // Rafraîchir le token avant son expiration
        this.keycloak.onTokenExpired = () => {
          this.keycloak.updateToken(70).catch(() => {
            console.warn('Token refresh failed, attempting silent re-auth...');
            this.silentReAuth();
          });
        };

        // Mettre à jour le token du socket après un refresh réussi
        this.keycloak.onAuthRefreshSuccess = () => {
          const socketStore = useSocketStore();
          socketStore.refreshToken(this.keycloak.token);
        };

        // Rafraîchir le token périodiquement (toutes les 4 minutes)
        this._tokenRefreshInterval = setInterval(() => {
          if (this.keycloak?.authenticated) {
            this.keycloak.updateToken(300).catch(() => {
              console.warn('Periodic token refresh failed');
            });
          }
        }, 4 * 60 * 1000);

        // Rafraîchir le token quand l'utilisateur revient sur l'onglet
        this._visibilityHandler = () => {
          if (document.visibilityState === 'visible' && this.keycloak?.authenticated) {
            this.keycloak.updateToken(30).catch(() => {
              console.warn('Visibility token refresh failed, attempting silent re-auth...');
              this.silentReAuth();
            });
          }
        };
        document.addEventListener('visibilitychange', this._visibilityHandler);
      } else {
        console.log('Keycloak not authenticated');
        this.keycloak.login();
      }
    },

    silentReAuth() {
      // Tente une ré-authentification silencieuse via la session SSO Keycloak
      // Si la session SSO est encore active, l'utilisateur ne verra rien
      // Sinon, il sera redirigé vers la page de login
      this.keycloak.login({ prompt: 'none' }).catch(() => {
        // prompt=none a échoué (session SSO expirée), login classique
        this.keycloak.login();
      });
    },

    async editProfile() {
      // Rediriger vers la page de profil Keycloak
      if (this.keycloak) {
        const referrerUri = encodeURIComponent(window.location.href);
        const profileUrl = `${this.keycloak.authServerUrl}/realms/${this.keycloak.realm}/account?referrer=${this.keycloak.clientId}&referrer_uri=${referrerUri}`;
        globalThis.location.href = profileUrl;
      }
    },

    async setLanguage(lang) {
      this.locale = lang;
      i18n.global.locale = lang;
      localStorage.setItem('locale', lang);

      try {
        const profile = await this.keycloak.loadUserProfile();

        // Mettre à jour via l'API Keycloak
        await fetch(`${this.keycloak.authServerUrl}/admin/realms/${this.keycloak.realm}/users/${profile.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.keycloak.token}`,
          },
          body: JSON.stringify({
            ...profile,
            attributes: {
              ...profile.attributes,
              locale: [lang],
            },
          }),
        });
      } catch (error) {
        console.warn('Failed to persist locale to Keycloak:', error);
      }
    },
  },
});
